import JSZip from 'jszip';
import { adminExpenseApi } from './api.js';

// Sanitize a path segment so it's safe for ZIP entries / OS filesystems.
function safe(s) {
  return String(s || '').replace(/[^a-zA-Z0-9_.-]/g, '_').slice(0, 80) || 'item';
}

// Single-report bundle: just download what the server produced.
// The previous version of this helper called the export endpoint for
// the XLSX and then fetch()ed each receipt blob URL cross-origin to
// add it to a frontend-built ZIP. Azurite (and any blob storage
// without CORS configured) blocks browser fetch() — so the receipts/
// folder ended up empty even though the viewer (which uses <img> /
// <iframe> and is exempt from CORS) saw the receipts fine.
//
// Server-side bundling sidesteps the issue entirely: backend reads the
// blobs internally with getReceiptBuffer and streams a ZIP back over
// /api/... — same-origin via the SWA proxy.
export async function buildSingleReportBundle({ userId, reportId, displayName, claimRef }) {
  const blob = await adminExpenseApi.bundleExpenseReport(userId, reportId);
  const baseName = `${safe(displayName || 'Report')}_${safe(claimRef || reportId)}`;
  return { blob, fileName: `${baseName}.zip` };
}

// Bulk bundle: fetch each report's server-built bundle, unwrap into a
// per-report folder of the parent ZIP. Each per-report ZIP is opened
// and re-emitted as a flat folder so users get one tidy structure
// (`<Person_Ref>/<XLSX>` + `<Person_Ref>/receipts/...`) instead of a
// ZIP-of-ZIPs they have to extract twice.
export async function buildBulkBundle(reports, opts = {}) {
  const onWarn = opts.onWarn || (() => {});
  const onProgress = opts.onProgress || (() => {});

  const parent = new JSZip();
  let included = 0;

  for (let i = 0; i < reports.length; i++) {
    const report = reports[i];
    onProgress({ current: i + 1, total: reports.length, report });
    if (!report.rowKey) continue;

    const folderName = `${safe(report.requesterName || 'Report')}_${safe(report.claimRef || report.rowKey)}`;

    try {
      const subBlob = await adminExpenseApi.bundleExpenseReport(report.partitionKey, report.rowKey);
      const subZip = await JSZip.loadAsync(subBlob);
      const folder = parent.folder(folderName);

      // Copy every entry from the per-report ZIP into the parent
      // folder, preserving sub-paths (so receipts/foo.jpg stays under
      // `<folderName>/receipts/foo.jpg`).
      const entries = Object.values(subZip.files);
      for (const entry of entries) {
        if (entry.dir) continue;
        const data = await entry.async('uint8array');
        folder.file(entry.name, data);
      }
      included++;
    } catch (err) {
      onWarn(`Report ${report.claimRef || report.rowKey} failed: ${err.message}`);
    }
  }

  if (included === 0) return { blob: null, fileName: null, included: 0 };

  const blob = await parent.generateAsync({ type: 'blob' });
  const fileName = `expense-reports-${new Date().toISOString().split('T')[0]}.zip`;
  return { blob, fileName, included };
}

// Tiny browser helper — saves a blob with the given file name.
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
